/**
 * The delivery path (ADR-022): the intended driving line through the road
 * network, from the start to the customer. It gives every world point a
 * path frame — progress `s` (m along the path) and lateral offset `d`
 * (m to the RIGHT of the travel direction) — which replaces the old
 * "forward = +z" assumptions: encounter zones, commitment offsets, stuck
 * progress, distance-to-go, and the turn guidance are all expressed in (s, d).
 *
 * Built from a waypoint polyline with corner fillets, then sampled densely.
 * Pure module: no DOM, no Babylon.
 */

import { clamp, wrapAngle, type Vec2 } from "../core/vec";

export type TurnKind = "left" | "right" | "roundabout" | "arrive";

export interface TurnCue {
  /** Path progress of the maneuver point. */
  s: number;
  kind: TurnKind;
  /** Roundabout: which exit to take (1-based). */
  exit?: number;
  label: string;
}

export interface PathWaypoint extends Vec2 {
  /** Corridor half-width used for off-path detection (m). */
  halfWidth: number;
  /** Corner fillet radius at this waypoint (m); 0 = sharp. */
  fillet?: number;
  /** Optional anchor name so config can reference "progress at this waypoint". */
  name?: string;
  /** Road piece id this waypoint belongs to (for section bookkeeping). */
  piece?: string;
}

export interface PathPoint {
  x: number;
  z: number;
  /** Travel direction (yaw, rad from +z toward +x). */
  theta: number;
  halfWidth: number;
  piece: string;
}

export interface PathFrame {
  s: number;
  /** Lateral offset, positive to the right of travel. */
  d: number;
  theta: number;
  halfWidth: number;
  piece: string;
}

const SAMPLE_STEP = 0.25;

/** Replace each interior corner by a tangent arc of the requested radius. */
function filletPolyline(wps: PathWaypoint[]): Array<PathWaypoint & { anchor?: string }> {
  const out: Array<PathWaypoint & { anchor?: string }> = [];
  for (let i = 0; i < wps.length; i++) {
    const w = wps[i]!;
    const r = w.fillet ?? 0;
    if (i === 0 || i === wps.length - 1 || r <= 0) {
      out.push({ ...w, anchor: w.name });
      continue;
    }
    const a = wps[i - 1]!;
    const b = wps[i + 1]!;
    const inX = w.x - a.x;
    const inZ = w.z - a.z;
    const outX = b.x - w.x;
    const outZ = b.z - w.z;
    const inLen = Math.hypot(inX, inZ);
    const outLen = Math.hypot(outX, outZ);
    const ux = inX / inLen;
    const uz = inZ / inLen;
    const vx = outX / outLen;
    const vz = outZ / outLen;
    const turn = wrapAngle(Math.atan2(vx, vz) - Math.atan2(ux, uz));
    if (Math.abs(turn) < 1e-3) {
      out.push({ ...w, anchor: w.name });
      continue;
    }
    // Tangent length from the corner to each tangent point; clamp the radius
    // so the fillet never eats more than ~45% of either leg.
    let rr = r;
    let tan = rr * Math.tan(Math.abs(turn) / 2);
    const maxTan = Math.min(inLen, outLen) * 0.45;
    if (tan > maxTan) {
      tan = maxTan;
      rr = tan / Math.tan(Math.abs(turn) / 2);
    }
    const p0x = w.x - ux * tan;
    const p0z = w.z - uz * tan;
    // Arc center: offset from p0 along the inside normal.
    const sign = turn > 0 ? 1 : -1; // right turn (toward +x from +z) => +
    const nx = sign * uz; // right normal of (ux, uz) is (uz, -ux)
    const nz = -sign * ux;
    const cx = p0x + nx * rr;
    const cz = p0z + nz * rr;
    const a0 = Math.atan2(p0x - cx, p0z - cz);
    const steps = Math.max(4, Math.ceil((Math.abs(turn) * rr) / SAMPLE_STEP));
    for (let k = 0; k <= steps; k++) {
      const ang = a0 + (turn * k) / steps; // ring angle advances with the turn sign
      out.push({
        x: cx + Math.sin(ang) * rr,
        z: cz + Math.cos(ang) * rr,
        halfWidth: w.halfWidth,
        piece: w.piece,
        anchor: k === 0 ? w.name : undefined,
      });
    }
  }
  return out;
}

export class DeliveryPath {
  readonly length: number;
  readonly turns: TurnCue[];
  private readonly sx: Float64Array;
  private readonly sz: Float64Array;
  private readonly ss: Float64Array;
  private readonly sth: Float64Array;
  private readonly shw: Float64Array;
  private readonly spiece: string[];
  private readonly anchors = new Map<string, number>();

  constructor(waypoints: PathWaypoint[], turns: Array<Omit<TurnCue, "s"> & { at: string }>) {
    if (waypoints.length < 2) throw new Error("path needs >= 2 waypoints");
    const poly = filletPolyline(waypoints);
    // Cumulative length over the polyline.
    const cum: number[] = [0];
    for (let i = 1; i < poly.length; i++) {
      cum.push(cum[i - 1]! + Math.hypot(poly[i]!.x - poly[i - 1]!.x, poly[i]!.z - poly[i - 1]!.z));
    }
    this.length = cum[cum.length - 1]!;
    for (let i = 0; i < poly.length; i++) {
      const a = poly[i]!.anchor;
      if (a) this.anchors.set(a, cum[i]!);
    }
    // Resample at a fixed step.
    const n = Math.max(2, Math.ceil(this.length / SAMPLE_STEP) + 1);
    this.sx = new Float64Array(n);
    this.sz = new Float64Array(n);
    this.ss = new Float64Array(n);
    this.sth = new Float64Array(n);
    this.shw = new Float64Array(n);
    this.spiece = new Array<string>(n);
    let seg = 0;
    for (let k = 0; k < n; k++) {
      const s = Math.min(this.length, (k * this.length) / (n - 1));
      while (seg + 2 < poly.length && cum[seg + 1]! < s) seg++;
      const a = poly[seg]!;
      const b = poly[seg + 1]!;
      const segLen = cum[seg + 1]! - cum[seg]!;
      const u = segLen < 1e-9 ? 0 : clamp((s - cum[seg]!) / segLen, 0, 1);
      this.sx[k] = a.x + (b.x - a.x) * u;
      this.sz[k] = a.z + (b.z - a.z) * u;
      this.ss[k] = s;
      this.sth[k] = Math.atan2(b.x - a.x, b.z - a.z);
      this.shw[k] = a.halfWidth + (b.halfWidth - a.halfWidth) * u;
      // A leg leaving a junction corner (J*) belongs to the road it enters;
      // every other sample keeps its segment's start label (fillet arcs carry
      // the corner's own label).
      const aPiece = a.piece ?? "";
      const bPiece = b.piece ?? aPiece;
      this.spiece[k] = aPiece.startsWith("J") && bPiece !== aPiece ? bPiece : aPiece || bPiece;
    }
    // Smooth theta slightly so fillet sampling does not introduce steps.
    const sm = new Float64Array(n);
    for (let k = 0; k < n; k++) {
      let sx = 0;
      let cx = 0;
      for (let j = -2; j <= 2; j++) {
        const idx = clamp(k + j, 0, n - 1);
        sx += Math.sin(this.sth[idx]!);
        cx += Math.cos(this.sth[idx]!);
      }
      sm[k] = Math.atan2(sx, cx);
    }
    this.sth.set(sm);
    this.turns = turns.map((t) => {
      const s = this.anchors.get(t.at);
      if (s === undefined) throw new Error(`unknown path anchor ${t.at}`);
      const { at: _at, ...rest } = t;
      return { ...rest, s };
    });
    this.turns.sort((a, b) => a.s - b.s);
  }

  /** Progress of a named waypoint anchor. */
  /** Progress at a named waypoint, or null when the map has no such anchor. */
  anchorOrNull(name: string): number | null {
    return this.anchors.get(name) ?? null;
  }

  /** Every named anchor with its progress (for derived route metadata). */
  anchorList(): ReadonlyArray<[string, number]> {
    return [...this.anchors.entries()];
  }

  anchor(name: string): number {
    const s = this.anchors.get(name);
    if (s === undefined) throw new Error(`unknown path anchor ${name}`);
    return s;
  }

  /** Point on the path at progress s (clamped). */
  point(s: number): PathPoint {
    const n = this.ss.length;
    const u = clamp(s / this.length, 0, 1) * (n - 1);
    const k = Math.min(n - 2, Math.floor(u));
    const f = u - k;
    return {
      x: this.sx[k]! + (this.sx[k + 1]! - this.sx[k]!) * f,
      z: this.sz[k]! + (this.sz[k + 1]! - this.sz[k]!) * f,
      theta: this.sth[f < 0.5 ? k : k + 1]!,
      halfWidth: this.shw[k]! + (this.shw[k + 1]! - this.shw[k]!) * f,
      piece: this.spiece[f < 0.5 ? k : k + 1]!,
    };
  }

  /**
   * Path frame of a world point. `sHint` (the previous progress) restricts
   * the search to a window so the ring's overlapping arcs can never alias;
   * without it the whole path is searched.
   */
  project(x: number, z: number, sHint: number | null = null, window = 25): PathFrame {
    const n = this.ss.length;
    let lo = 0;
    let hi = n - 1;
    if (sHint !== null) {
      lo = clamp(Math.floor(((sHint - window) / this.length) * (n - 1)), 0, n - 1);
      hi = clamp(Math.ceil(((sHint + window) / this.length) * (n - 1)), 0, n - 1);
    }
    let best = lo;
    let bestD = Infinity;
    for (let k = lo; k <= hi; k++) {
      const dx = x - this.sx[k]!;
      const dz = z - this.sz[k]!;
      const d2 = dx * dx + dz * dz;
      if (d2 < bestD) {
        bestD = d2;
        best = k;
      }
    }
    // Refine onto the neighbouring segments.
    let s = this.ss[best]!;
    let px = this.sx[best]!;
    let pz = this.sz[best]!;
    let bestDist = Math.sqrt(bestD);
    for (const k of [best - 1, best]) {
      if (k < 0 || k + 1 >= n) continue;
      const ax = this.sx[k]!;
      const az = this.sz[k]!;
      const bx = this.sx[k + 1]!;
      const bz = this.sz[k + 1]!;
      const vx = bx - ax;
      const vz = bz - az;
      const vv = vx * vx + vz * vz;
      if (vv < 1e-12) continue;
      const u = clamp(((x - ax) * vx + (z - az) * vz) / vv, 0, 1);
      const qx = ax + vx * u;
      const qz = az + vz * u;
      const dist = Math.hypot(x - qx, z - qz);
      if (dist < bestDist - 1e-9) {
        bestDist = dist;
        s = this.ss[k]! + (this.ss[k + 1]! - this.ss[k]!) * u;
        px = qx;
        pz = qz;
      }
    }
    const p = this.point(s);
    // Right normal of heading theta is (cos theta, -sin theta).
    const d = (x - px) * Math.cos(p.theta) - (z - pz) * Math.sin(p.theta);
    return { s, d, theta: p.theta, halfWidth: p.halfWidth, piece: p.piece };
  }

  /** World point at (s, d). */
  offsetPoint(s: number, d: number): Vec2 {
    const p = this.point(s);
    return { x: p.x + Math.cos(p.theta) * d, z: p.z - Math.sin(p.theta) * d };
  }

  /** The next turn cue at or after progress s (tolerating `slack` m past it). */
  nextTurn(s: number, slack = 2): TurnCue | null {
    for (const t of this.turns) if (t.s >= s - slack) return t;
    return null;
  }
}
